import { formatTenantMoney } from '../../lib/tenantCurrency';

/**
 * Turning numbers into the words on a card.
 *
 * Every function here is pure and tested — a dashboard is a wall of formatted
 * numbers, and the ways they go wrong (a negative delta rendered as growth, a
 * rate shown as 0.0743) are silent.
 */

/** 1,204 · 12.4k · 3.1M — a card has room for four characters, not nine. */
export const compact = (value: number): string => {
  const number = Number(value) || 0;
  const sign = number < 0 ? '-' : '';
  const abs = Math.abs(number);
  if (abs < 1000) return `${sign}${Math.round(abs)}`;
  if (abs < 10000) return `${sign}${(abs / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  if (abs < 1000000) return `${sign}${Math.round(abs / 1000)}k`;
  return `${sign}${(abs / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
};

/** The exact number, grouped, for tooltips and tables where space allows. */
export const exact = (value: number): string => (Number(value) || 0).toLocaleString();

/** 0.0743 -> "7.4%". Rates arrive from the API as fractions, always. */
export const percent = (fraction: number, digits = 1): string =>
  `${((Number(fraction) || 0) * 100).toFixed(digits)}%`;

/**
 * A duration a person reads at a glance: 48s, 4m 12s, 1h 03m.
 *
 * Engaged time is usually under two minutes, so seconds are kept at that scale
 * and dropped above an hour where they are noise.
 */
export const duration = (seconds: number): string => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  if (minutes < 60) return rest ? `${minutes}m ${String(rest).padStart(2, '0')}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`;
};

/** A headline amount in the tenant's currency, to the whole unit. */
export const money = (value: number): string => formatTenantMoney(value, 0);

export interface Delta {
  /** The signed fraction, or null when there is nothing to compare against. */
  ratio: number | null;
  label: string;
  direction: 'up' | 'down' | 'flat' | 'none';
}

/**
 * The change against the comparison period.
 *
 * Growth from zero is NOT infinity and must not render as one — it is "new",
 * because a percentage against nothing is a number with no meaning. And a
 * direction is deliberately separate from whether that direction is GOOD:
 * bounce rate going up is a rise and a problem, and only the card knows which.
 */
export const delta = (current: number, previous: number | null | undefined): Delta => {
  if (previous === null || previous === undefined) return { ratio: null, label: '—', direction: 'none' };
  const now = Number(current) || 0;
  const before = Number(previous) || 0;
  if (before === 0) {
    if (now === 0) return { ratio: 0, label: 'no change', direction: 'flat' };
    return { ratio: null, label: 'new', direction: 'up' };
  }
  const ratio = (now - before) / before;
  if (Math.abs(ratio) < 0.005) return { ratio: 0, label: 'no change', direction: 'flat' };
  return {
    ratio,
    label: `${ratio > 0 ? '+' : ''}${(ratio * 100).toFixed(ratio > -0.1 && ratio < 0.1 ? 1 : 0)}%`,
    direction: ratio > 0 ? 'up' : 'down',
  };
};

/** Whether a rise is good news. Bounce rate rising is not. */
export const toneOf = (direction: Delta['direction'], higherIsBetter = true): 'good' | 'bad' | 'flat' => {
  if (direction === 'flat' || direction === 'none') return 'flat';
  const rising = direction === 'up';
  return rising === higherIsBetter ? 'good' : 'bad';
};

const CHANNEL_LABELS: Record<string, string> = {
  direct: 'Direct',
  organic: 'Organic search',
  paid: 'Paid',
  social: 'Social',
  email: 'Email',
  referral: 'Referral',
  internal: 'Internal',
};
export const channelLabel = (channel: string): string => CHANNEL_LABELS[channel] || channel;

const DIMENSION_LABELS: Record<string, string> = {
  page: 'Top pages',
  entry_page: 'Landing pages',
  exit_page: 'Exit pages',
  referrer: 'Referrers',
  channel: 'Channels',
  country: 'Countries',
  region: 'Regions',
  city: 'Cities',
  device: 'Devices',
  os: 'Operating systems',
  browser: 'Browsers',
  language: 'Languages',
  utm_source: 'Campaign sources',
  utm_medium: 'Campaign mediums',
  utm_campaign: 'Campaigns',
  app_version: 'App versions',
};
export const dimensionLabel = (dim: string): string => DIMENSION_LABELS[dim] || dim;

/**
 * The same dimension as a COLUMN header rather than as the name of a list.
 * `dimensionLabel` names the whole ranked list and is plural for it; the cell
 * under the header holds one of them, and "Campaigns: black-car-jfk" reads as a
 * count of something.
 */
const DIMENSION_COLUMNS: Record<string, string> = {
  page: 'Page',
  entry_page: 'Landing page',
  exit_page: 'Exit page',
  referrer: 'Referrer',
  channel: 'Channel',
  country: 'Country',
  region: 'Region',
  city: 'City',
  device: 'Device',
  os: 'Operating system',
  browser: 'Browser',
  language: 'Language',
  utm_source: 'Source',
  utm_medium: 'Medium',
  utm_campaign: 'Campaign',
  app_version: 'App version',
};
export const dimensionColumn = (dim: string): string => DIMENSION_COLUMNS[dim] || dimensionLabel(dim);

/** ISO country code to a flag, for the geo list. Falls back to the code. */
export const flagOf = (code: string | null): string => {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) return '';
  const base = 0x1f1e6;
  return String.fromCodePoint(
    ...code
      .toUpperCase()
      .split('')
      .map((letter) => base + letter.charCodeAt(0) - 65),
  );
};

/**
 * `2026-03-08` -> `8 Mar`, for an axis that has to fit twelve of them.
 *
 * Deliberately NOT the tenant's date pattern, and the one exception to the rule
 * that every date goes through `useTimeFormat()`. This is an axis TICK, not a
 * timestamp: `08/03/2026` twelve times across a chart is unreadable, and the
 * input is already a civil day string — there is no instant here to put in a
 * zone. The timestamps in the tables below the charts do follow the tenant.
 */
export const shortDay = (day: string): string => {
  // A full instant is tolerated as well as the civil day the wire promises,
  // because the two halves of this repo deploy on separate rails: a console
  // pushed ahead of the service is talking to one whose `date` columns still
  // arrive as `2026-09-13T00:00:00.000Z`, and an axis of those is unreadable
  // (it is what this function was reported broken for). The service-side fix is
  // the DATE type parser in db/knexfile.js.
  const civil = day.length > 10 ? day.slice(0, 10) : day;
  const parsed = new Date(`${civil}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return day;
  return `${parsed.getUTCDate()} ${parsed.toLocaleString('en', { month: 'short', timeZone: 'UTC' })}`;
};

export const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
